import { Query, QueryRowType } from "@rocicorp/zero";
import { z } from "zod/v4";
import { type FormSchemaType, zFormIntNullable } from "@corpuscomplete/core/utils";
import { Schema, zql } from "../../zero/schema";
import { Food } from "../food";
import { Nutrient } from "../nutrient";

export namespace RecipeBase {
  export const MAX_QUANTITY = 999_999;
  export const MAX_TIME_MINUTES = 10_080;
  export const MAX_CURATED_VARIANTS = 10;

  export const formSchema = (type: FormSchemaType) =>
    z.object({
      base: Food.formBaseSchema(type),
      categoryAssociations: Food.formCategoryAssociationsSchema(type),
      recipe: z.object({
        description: z.string(),
        prepTimeMinutes: zFormIntNullable(type),
        cookTimeMinutes: zFormIntNullable(type),
      }),
      ingredients: z
        .object({
          amount: z.number().positive().max(MAX_QUANTITY),
          foodServingId: z.uuid(),
        })
        .array()
        .min(type === "validation" ? 1 : 0, {
          message: "At least one ingredient is required",
        }),
      steps: z
        .object({
          text: z.string(),
        })
        .array(),
    });
  export type Form = z.infer<ReturnType<typeof formSchema>>;

  export const formDefault = (): Form => ({
    base: Food.formBaseDefault(),
    categoryAssociations: Food.formCategoryAssociationsDefault(),
    recipe: {
      description: "",
      prepTimeMinutes: "",
      cookTimeMinutes: "",
    },
    steps: [
      {
        text: "",
      },
    ],
    ingredients: [],
  });

  export type PerRecipeServing = Food.Serving & {
    perRecipe: number;
  };

  export function query<Q extends Query<"recipe", Schema, any> = Query<"recipe", Schema>>(q?: Q) {
    let query = ((q ?? zql.recipe) as Query<"recipe", Schema>)
      .related("base", (q) =>
        q
          .related("meta")
          .related("categoryAssociations")
          .related("servings", (q) => q.orderBy("sortIndex", "asc")),
      )
      .related("serving")
      .related("steps", (q) => q.orderBy("sortIndex", "asc"))
      .related("ingredients", (q) =>
        q
          .orderBy("sortIndex", "asc")
          .related("serving", (q) => q.related("food", (q) => q.related("meta"))),
      );
    return query;
  }
  export type RawInfo = QueryRowType<ReturnType<typeof query>>;

  export function asInfo(raw: RawInfo | undefined): Info | undefined {
    if (!raw?.base?.meta) {
      return undefined;
    }
    let serving: PerRecipeServing | undefined;
    for (const baseServing of raw.base.servings) {
      if (raw.serving?.id === baseServing.id) {
        serving = {
          ...baseServing,
          perRecipe: raw.serving.perRecipe,
        };
        break;
      }
    }
    if (!serving) {
      return undefined;
    }

    return {
      id: raw.id,
      version: raw.version,
      cookTimeMinutes: raw.cookTimeMinutes,
      description: raw.description,
      prepTimeMinutes: raw.prepTimeMinutes,
      serving,
      ingredients: Food.asItems(raw.ingredients),
      steps: raw.steps,
      base: raw.base,
      meta: {
        ...raw.base.meta,
        isArchived: raw.base.meta.timeArchived !== null,
      },
      categoryAssociations: raw.base.categoryAssociations,
    };
  }

  export type Info = Omit<RawInfo, "base" | "ingredients" | "serving"> & {
    base: Food.BaseInput;
    meta: Food.MetaInput & { isArchived: boolean };
    categoryAssociations: readonly Omit<Food.CategoryAssociation, "foodId" | "foodVersion">[];
    ingredients: readonly Food.AsItem<RawInfo["ingredients"][number]>[];
    serving: PerRecipeServing;
  };

  // TODO: Consider if this should be moved to the Food module
  type IngredientNutrientInput = Pick<Food.ItemInput, "amount" | "foodServingId">;

  const ingredientsToFoodItems = (
    ingredients: readonly IngredientNutrientInput[],
    servings: readonly Food.Serving[],
  ): Pick<Food.Item, "amount" | "serving">[] => {
    const servingMap = new Map(servings.map((serving) => [serving.id, serving]));
    const items: Pick<Food.Item, "amount" | "serving">[] = [];

    for (const ingredient of ingredients) {
      const serving = servingMap.get(ingredient.foodServingId);
      if (!serving) {
        throw new Error(`Food serving not found. foodServingId: "${ingredient.foodServingId}"`);
      }
      items.push({ amount: ingredient.amount, serving });
    }

    return items;
  };

  export const ingredientNutrients = (
    ingredients: readonly IngredientNutrientInput[],
    servings: readonly Food.Serving[],
  ): Nutrient.Amounts => {
    return Nutrient.addFoodItems(
      Nutrient.emptyAmounts(),
      ingredientsToFoodItems(ingredients, servings),
    );
  };
}
